<template>
  <div class="login-container">
    <el-form ref="loginForm" :model="loginForm" :rules="currentRules" class="login-form" auto-complete="on"
      label-position="left">
      <h3 class="title">申诚数联催收系统</h3>

      <!-- 手机号（两种模式共用） -->
      <el-form-item prop="mobile">
        <span class="svg-container">
          <i class="el-icon-mobile-phone"></i>
        </span>
        <el-input v-model.trim="loginForm.mobile" name="mobile" type="text" placeholder="手机号" auto-complete="on" />
      </el-form-item>

      <!-- 短信验证码模式 -->
      <template v-if="mode === 'sms'">
        <el-form-item prop="code">
          <span class="svg-container">
            <i class="el-icon-message"></i>
          </span>
          <el-input v-model.trim="loginForm.code" name="code" type="text" placeholder="6 位短信验证码" maxlength="6"
            @keyup.enter.native="handleLogin" style="width: 55%;" />
          <el-button :disabled="cooldown > 0 || sending" :loading="sending" type="primary" plain
            style="margin-left: 8px; width: 28%;" @click="onSendSms">
            {{ cooldown > 0 ? cooldown + 's' : '获取验证码' }}
          </el-button>
        </el-form-item>
      </template>

      <!-- 密码模式 -->
      <template v-else>
        <el-form-item prop="password">
          <span class="svg-container">
            <i class="el-icon-lock"></i>
          </span>
          <el-input :type="pwdType" v-model.trim="loginForm.password" name="password" placeholder="密码"
            auto-complete="on" @keyup.enter.native="handleLogin" />
          <span class="show-pwd" @click="showPwd">
            <i class="el-icon-view"></i>
          </span>
        </el-form-item>
      </template>

      <el-form-item>
        <el-button :loading="loading" type="primary" style="width:100%;" @click.native.prevent="handleLogin">
          登录
        </el-button>
      </el-form-item>

      <div class="switch-mode" v-if="passwordLoginEnabled || mode === 'password'">
        <span @click="toggleMode">{{ mode === 'sms' ? '使用密码登录' : '使用短信验证码登录' }}</span>
      </div>
    </el-form>

    <el-dialog title="请输入图形验证码" :visible.sync="captchaDialogVisible" width="320px" :close-on-click-modal="false"
      append-to-body>
      <div style="display: flex; align-items: center; gap: 10px;">
        <el-input v-model.trim="captchaAnswer" placeholder="图形码" maxlength="6" style="flex: 1;"
          :disabled="sending" @keyup.enter.native="onCaptchaConfirm"></el-input>
        <img v-if="captchaImg" :src="captchaImg" alt="captcha" style="height: 38px; cursor: pointer;"
          title="点击换一张" @click="refreshCaptcha" />
      </div>
      <div slot="footer">
        <el-button :disabled="sending" @click="captchaDialogVisible = false">取消</el-button>
        <el-button type="primary" :disabled="sending" :loading="sending" @click="onCaptchaConfirm">确定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { fetchCaptcha, sendSmsCode, getLoginConfig } from '@/api/auth.js'

export default {
  name: 'Login',
  data() {
    const validateMobile = (rule, value, callback) => {
      if (!/^1\d{10}$/.test(value)) {
        callback(new Error('请输入 11 位手机号'))
      } else {
        callback()
      }
    }
    const validateCode = (rule, value, callback) => {
      if (!/^\d{4,6}$/.test(value)) {
        callback(new Error('请输入验证码'))
      } else {
        callback()
      }
    }
    const validatePass = (rule, value, callback) => {
      if (value.length < 5) {
        callback(new Error('密码不能小于5位'))
      } else {
        callback()
      }
    }
    return {
      mode: 'sms', // 'sms' | 'password'
      passwordLoginEnabled: false,
      loginForm: {
        mobile: '',
        code: '',
        password: ''
      },
      smsRules: {
        mobile: [{ required: true, trigger: 'blur', validator: validateMobile }],
        code: [{ required: true, trigger: 'blur', validator: validateCode }]
      },
      pwdRules: {
        mobile: [{ required: true, trigger: 'blur', validator: validateMobile }],
        password: [{ required: true, trigger: 'blur', validator: validatePass }]
      },
      loading: false,
      sending: false,
      cooldown: 0,
      cooldownTimer: null,
      redirect: undefined,
      pwdType: 'password',

      captchaDialogVisible: false,
      captchaImg: '',
      captchaSession: '',
      captchaAnswer: '',
      captchaBlobUrl: ''
    }
  },
  computed: {
    currentRules() {
      return this.mode === 'sms' ? this.smsRules : this.pwdRules
    }
  },
  watch: {
    $route: {
      handler: function (route) {
        this.redirect = route.query && route.query.redirect
      },
      immediate: true
    }
  },
  beforeDestroy() {
    this.clearCaptchaBlob()
    if (this.cooldownTimer) clearInterval(this.cooldownTimer)
  },
  mounted() {
    getLoginConfig().then(resp => {
      if (resp && resp.data) {
        this.passwordLoginEnabled = !!resp.data.passwordLoginEnabled
      }
    }).catch(() => {})
  },
  methods: {
    toggleMode() {
      this.mode = this.mode === 'sms' ? 'password' : 'sms'
      this.$nextTick(() => {
        this.$refs.loginForm.clearValidate()
      })
    },
    showPwd() {
      this.pwdType = this.pwdType === 'password' ? '' : 'password'
    },
    startCooldown() {
      this.cooldown = 60
      if (this.cooldownTimer) clearInterval(this.cooldownTimer)
      this.cooldownTimer = setInterval(() => {
        this.cooldown -= 1
        if (this.cooldown <= 0) {
          clearInterval(this.cooldownTimer)
          this.cooldownTimer = null
        }
      }, 1000)
    },
    clearCaptchaBlob() {
      if (this.captchaBlobUrl) {
        try { window.URL.revokeObjectURL(this.captchaBlobUrl) } catch (e) {}
        this.captchaBlobUrl = ''
      }
    },
    async refreshCaptcha() {
      this.clearCaptchaBlob()
      try {
        const result = await fetchCaptcha()
        if (result) {
          this.captchaImg = result.blobUrl
          this.captchaBlobUrl = result.blobUrl
          this.captchaSession = result.sessionKey
          this.captchaAnswer = ''
        }
      } catch (e) {
        this.$message.error('获取图形码失败')
      }
    },
    async onSendSms() {
      if (this.sending) return
      if (!/^1\d{10}$/.test(this.loginForm.mobile)) {
        this.$message.warning('请先填入手机号')
        return
      }
      this.sending = true
      try {
        const resp = await sendSmsCode(this.loginForm.mobile, this.captchaSession, this.captchaAnswer)
        const data = (resp && resp.data) || {}
        if (data.captchaRequired) {
          if (data.captchaWrong) {
            this.$message.error('图形验证码错误，请重新输入')
          }
          await this.refreshCaptcha()
          this.captchaDialogVisible = true
          return
        }
        this.captchaDialogVisible = false
        this.clearCaptchaBlob()
        this.captchaSession = ''
        this.captchaAnswer = ''
        this.$message.success('验证码已发送')
        this.startCooldown()
      } catch (e) {
        // 错误已被全局拦截器弹 toast
      } finally {
        this.sending = false
      }
    },
    async onCaptchaConfirm() {
      if (this.sending) return
      if (!this.captchaAnswer) {
        this.$message.warning('请输入图形码')
        return
      }
      await this.onSendSms()
    },
    handleLogin() {
      this.$refs.loginForm.validate(valid => {
        if (valid) {
          this.loading = true
          if (this.mode === 'sms') {
            this.$store.dispatch('SmsLogin', {
              mobile: this.loginForm.mobile,
              code: this.loginForm.code
            }).then(() => {
              this.loading = false
              this.$router.push({ path: this.redirect || '/' })
            }).catch(() => {
              this.loading = false
            })
          } else {
            this.$store.dispatch('Login', {
              username: this.loginForm.mobile,
              password: this.loginForm.password
            }).then(() => {
              this.loading = false
              this.$router.push({ path: this.redirect || '/' })
            }).catch(() => {
              this.loading = false
            })
          }
        } else {
          return false
        }
      })
    }
  }
}
</script>

<style rel="stylesheet/scss" lang="scss">
$bg: #2d3a4b;
$light_gray: #eee;

/* reset element-ui css */
.login-container {
  .el-input {
    display: inline-block;
    height: 47px;
    width: 85%;

    input {
      background: transparent;
      border: 0px;
      -webkit-appearance: none;
      border-radius: 0px;
      padding: 12px 5px 12px 15px;
      color: $light_gray;
      height: 47px;

      &:-webkit-autofill {
        -webkit-box-shadow: 0 0 0px 1000px $bg inset !important;
        -webkit-text-fill-color: #fff !important;
      }
    }
  }

  .el-form-item {
    border: 1px solid rgba(255, 255, 255, 0.1);
    background: rgba(0, 0, 0, 0.1);
    border-radius: 5px;
    color: #454545;
  }
}
</style>

<style rel="stylesheet/scss" lang="scss" scoped>
$bg: #2d3a4b;
$dark_gray: #889aa4;
$light_gray: #eee;

.login-container {
  position: fixed;
  height: 100%;
  width: 100%;
  background-color: $bg;

  .login-form {
    position: absolute;
    left: 0;
    right: 0;
    width: 520px;
    max-width: 100%;
    padding: 35px 35px 15px 35px;
    margin: 120px auto;
  }

  .svg-container {
    padding: 6px 5px 6px 15px;
    color: $dark_gray;
    vertical-align: middle;
    width: 30px;
    display: inline-block;
    font-size: 20px;
  }

  .title {
    font-size: 26px;
    font-weight: 400;
    color: $light_gray;
    margin: 0px auto 40px auto;
    text-align: center;
    font-weight: bold;
  }

  .show-pwd {
    position: absolute;
    right: 10px;
    top: 7px;
    font-size: 16px;
    color: $dark_gray;
    cursor: pointer;
    user-select: none;
  }

  .switch-mode {
    text-align: center;
    margin-top: 10px;
    span {
      color: $dark_gray;
      font-size: 13px;
      cursor: pointer;
      &:hover {
        color: $light_gray;
      }
    }
  }
}
</style>
